<template>
  <div class="tab-grid-wrapper">
    <!-- Toolbar -->
    <div class="filter-row">
      <div class="filter-scroll">
        <div v-for="(f, fi) in heatmap.filters" :key="'hvf' + fi" class="filter-col">
          <div class="filter-label">{{ f.label }}</div>
          <RmSelect
            v-model="f.value"
            :items="f.items"
            multiple clearable
            :placeholder="f.placeholder"
            placeholder-color="#000000"
            class="filter-select"
          />
        </div>
      </div>
      <div class="filter-actions">
        <div class="settings-anchor">
          <button class="action-button-small" @click="toggleSettings" title="Settings">
            <v-icon small>mdi-cog</v-icon>
          </button>
          <div v-if="settingsOpen" class="settings-backdrop" @click="settingsOpen = false"></div>
          <div v-if="settingsOpen" class="settings-menu" :style="settingsMenuStyle">
            <div class="settings-menu-header">
              <span class="settings-menu-title">Settings</span>
              <v-icon small @click="settingsOpen = false" style="cursor: pointer;">mdi-close</v-icon>
            </div>
            <div class="settings-menu-body">
              <div class="settings-field">
                <div class="filter-label">Columns</div>
                <RmSelect
                  :items="heatmap.columnsAvailable"
                  :value="heatmap.columnProperty.target"
                  itemText="label" itemValue="target"
                  @input="setAxis('columnProperty', $event)"
                  class="filter-select"
                />
              </div>
              <div class="settings-field">
                <div class="filter-label">Rows</div>
                <RmSelect
                  :items="heatmap.columnsAvailable"
                  :value="heatmap.rowProperty.target"
                  itemText="label" itemValue="target"
                  @input="setAxis('rowProperty', $event)"
                  class="filter-select"
                />
              </div>
              <div class="settings-field">
                <label class="filter-label">
                  <input type="checkbox" v-model="heatmap.showInvalid" /> Show Invalid Documents
                </label>
              </div>
              <div class="settings-field">
                <label class="filter-label">
                  <input type="checkbox" v-model="heatmap.showEmptyColumns" /> Show Empty Columns
                </label>
              </div>
              <div class="settings-field">
                <label class="filter-label">
                  <input type="checkbox" v-model="heatmap.showEmptyRows" /> Show Empty Rows
                </label>
              </div>
            </div>
          </div>
        </div>
        <button class="action-button-small" @click="fetchDocumentGrid" title="Refresh">
          <v-icon small>mdi-refresh</v-icon>
        </button>
      </div>
    </div>

    <!-- Grid -->
    <div v-if="heatmap.loading" style="text-align: center; padding: 48px;">
      <div class="loader"></div>
      <span>Loading documents...</span>
    </div>
    <div v-else-if="!heatmap.documents.length" style="padding: 24px; color: #888; text-align: center;">
      No documents found. The document data pipeline is not yet connected.
    </div>
    <div v-else class="framework-grid-wrapper">
      <div class="framework-grid">
        <table class="grid-table heatmap-table">
          <thead>
            <tr class="header-row">
              <th class="header-cell level-header">{{ heatmap.rowProperty.label }}</th>
              <th v-for="col in columns" :key="col.key" class="header-cell track-header"
                  :style="heatmap.columnProperty.target === 'LF_TRACK' && col.background_colour ? { backgroundColor: col.background_colour } : {}">
                {{ col.label }}
                <v-icon v-if="!col.id && col.is_lft" small color="amber" :title="'Invalid ' + heatmap.columnProperty.label">mdi-alert</v-icon>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.key" class="heatmap-row">
              <td class="heatmap-row-label"
                :style="row.background_colour ? { backgroundColor: row.background_colour } : {}">
                {{ row.label }}
                <v-icon v-if="!row.id && row.is_lft" small color="amber" :title="'Invalid ' + heatmap.rowProperty.label">mdi-alert</v-icon>
              </td>
              <td v-for="col in columns" :key="col.key" class="heatmap-cell"
                :class="{ 'heatmap-cell-empty': !row.cells[col.key], 'heatmap-cell-clickable': row.cells[col.key] }"
                :style="cellStyle(row.cells[col.key])"
                @click="row.cells[col.key] && openCellPopup(row, col)">
                <template v-if="row.cells[col.key]">
                  <span class="heatmap-cell-count">{{ row.cells[col.key] }}</span>
                  <br/>
                  <span class="heatmap-cell-label">role{{ row.cells[col.key] > 1 ? 's' : '' }}</span>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <DocLevellingPopUp
      :show="docPopup.show"
      :details="docPopup.details"
      :attributes="heatmap.columnsAvailable"
      @close="docPopup.show = false"
    />
  </div>
</template>

<script>
import { ref, reactive, computed, watch, toRef } from 'vue';
import RmSelect from '@/components/common/RmSelect.vue';
import DocLevellingPopUp from '@/components/levelling/DocLevellingPopUp.vue';
import axios from "axios";
import utils from "@/common/utils.js";

export default {
  name: "HeatmapView",
  components: { RmSelect, DocLevellingPopUp },
  props: {
    levellingFramework: {
      type: Object,
      default: null,
    },
  },
  setup(props) {
    const settingsOpen = ref(false);
    const settingsMenuPos = reactive({ top: 0, right: 0 });

    const toggleSettings = (event) => {
      if (settingsOpen.value) {
        settingsOpen.value = false;
        return;
      }
      const rect = event.currentTarget.getBoundingClientRect();
      settingsMenuPos.top = rect.bottom + 4;
      settingsMenuPos.right = window.innerWidth - rect.right;
      settingsOpen.value = true;
    };

    const settingsMenuStyle = computed(() => ({
      top: settingsMenuPos.top + 'px',
      right: settingsMenuPos.right + 'px',
    }));

    const heatmap = reactive({
      loading: false,
      documents: [],
      columnProperty: { target: 'none', label: 'None' },
      rowProperty: { target: 'none', label: 'None' },
      filters: [],
      showInvalid: false,
      showEmptyRows: false,
      showEmptyColumns: true,
      columnsAvailable: [],
    });

    const filteredDocuments = computed(() => {
      let docs = heatmap.documents;
      const filters = heatmap.filters.filter(x => x.value?.length);
      if (filters.length)
        docs = docs.filter(d => filters.every(f => f.value.includes(d[f.target])));
      return heatmap.showInvalid ? docs : docs.filter(d => d.lft_abbreviation);
    });

    const axisValues = (column, axis) => {
      if (!column.valueList) return [];
      const valueList = heatmap.showInvalid ? column.valueList : column.valueList.filter(v => v.id || !column.is_lft);
      const vals = valueList.map(v => ({ key: v.value, label: v.value, position: v.position, background_colour: v.background_colour, text_colour: v.text_colour }));
      if (heatmap.filters.some(x => x.value)
          || (axis === "columns" && !heatmap.showEmptyColumns)
          || (axis === "rows" && !heatmap.showEmptyRows)) {
        const docs = filteredDocuments.value;
        return vals.filter(v => docs.some(d => d[column.target] === v.key));
      }
      return vals;
    };

    const columns = computed(() => axisValues(heatmap.columnProperty, "columns"));

    const rows = computed(() => {
      const rowVals = axisValues(heatmap.rowProperty, "rows");
      const cols = columns.value;
      const docs = filteredDocuments.value;
      const colField = heatmap.columnProperty.target;
      const rowField = heatmap.rowProperty.target;

      return rowVals.map(row => {
        const cells = {};
        for (const col of cols) {
          const count = docs.filter(d => d[rowField] === row.key && d[colField] === col.key).length;
          cells[col.key] = count;
        }
        return { ...row, cells };
      });
    });

    const maxCount = computed(() => {
      let max = 0;
      for (const row of rows.value) {
        for (const col of columns.value) {
          if (row.cells[col.key] > max) max = row.cells[col.key];
        }
      }
      return max;
    });

    const cellStyle = (count) => {
      if (!count) return {};
      // const RGB = [232, 234, 246];
      const RGB = [178, 216, 253];
      const intensity = maxCount.value > 0 ? count / maxCount.value : 0;
      const r = Math.round(RGB[0] - intensity * 100);
      const g = Math.round(RGB[1] - intensity * 90);
      const b = Math.round(RGB[2] - intensity * 10);
      return { backgroundColor: `rgb(${r}, ${g}, ${b})` };
    };

    const fetchDocumentGrid = () => {
      heatmap.loading = true;
      axios.get(`document/getLevellingFrameworkDocuments/${props.levellingFramework?.levelling_framework_id}`).then(resp => {
        if (resp.data.Status === 'OK' && resp.data.Data) {
          heatmap.documents = resp.data.Data.documents || [];
          heatmap.filters = [];
          heatmap.columnProperty = null;
          heatmap.rowProperty = null;
          heatmap.columnsAvailable = resp.data.Data.levellingAttributes.filter(x => x.present);
          heatmap.columnsAvailable.forEach(a => {
            heatmap.filters.push({
              value: null,
              items: a.valueList.map(v => ({ text: v.value, value: v.value })),
              placeholder: `All ${utils.pluralise(a.label)}`,
              label: a.label,
              target: a.target,
            });
            if (a.name === "LEVEL_GROUP") heatmap.columnProperty = a;
            if (a.name === "FAMILY") heatmap.rowProperty = a;
          });
          if (!heatmap.columnProperty) {
            heatmap.columnProperty = heatmap.columnsAvailable.find(c => c.target !== heatmap.rowProperty?.target) || heatmap.columnsAvailable[0];
          }
          if (!heatmap.rowProperty) {
            heatmap.rowProperty = heatmap.columnsAvailable.find(c => c.target !== heatmap.columnProperty?.target) || heatmap.columnsAvailable[0];
          }
        }
      }).finally(() => { heatmap.loading = false; });
    };

    watch(toRef(props, 'levellingFramework'), () => {
      fetchDocumentGrid();
    }, { immediate: true });

    const setAxis = (axis, target) => {
      const prev = heatmap[axis];
      if (axis === 'columnProperty' && target === heatmap.rowProperty.target) {
        heatmap.rowProperty = prev;
      } else if (axis === 'rowProperty' && target === heatmap.columnProperty.target) {
        heatmap.columnProperty = prev;
      }
      heatmap[axis] = heatmap.columnsAvailable.find(x => x.target === target);
    };

    const docPopup = reactive({ show: false, details: [] });

    const openCellPopup = (row, col) => {
      const rowField = heatmap.rowProperty.target;
      const colField = heatmap.columnProperty.target;
      const cellDocs = filteredDocuments.value.filter(d => d[rowField] === row.key && d[colField] === col.key);
      if (!cellDocs.length) return;

      const allDocs = filteredDocuments.value;
      docPopup.details = cellDocs.map(doc => {
        const famDocs = allDocs.filter(d => d.LF_FAMILY !== "Unknown" && d.LF_FAMILY === doc.LF_FAMILY && d.doc_id !== doc.doc_id && !isNaN(d.LF_LEVEL) && !isNaN(doc.LF_LEVEL))
          .sort((a, b) => Number(a.LF_LEVEL) - Number(b.LF_LEVEL));
        return {
          document: doc,
          related: {
            previous: famDocs.filter(d => Number(d.LF_LEVEL) < Number(doc.LF_LEVEL)),
            current: famDocs.filter(d => Number(d.LF_LEVEL) === Number(doc.LF_LEVEL)),
            next: famDocs.filter(d => Number(d.LF_LEVEL) > Number(doc.LF_LEVEL)),
          }
        };
      });
      docPopup.show = true;
    };

    return {
      settingsOpen,
      settingsMenuStyle,
      toggleSettings,
      heatmap,
      columns,
      rows,
      maxCount,
      cellStyle,
      filteredDocuments,
      fetchDocumentGrid,
      setAxis,
      docPopup,
      openCellPopup,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/vars";

.tab-grid-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.filter-row {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  border-radius: 8px;
  padding: 8px 0;
  margin: 0;
  gap: 8px;
}

.filter-scroll {
  display: flex;
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  gap: 8px;
  align-items: center;

  &::-webkit-scrollbar {
    height: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
  }
}

.filter-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-col {
  flex-shrink: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.filter-label {
  font-size: 14px;
  font-family: 'DM Sans' !important;
  font-weight: 400;
  color: #000000 !important;
  letter-spacing: 0;
  line-height: 140%;
  white-space: nowrap;
}

.filter-select {
  min-width: 180px;
  max-width: 280px;
}

.framework-grid-wrapper {
  overflow: auto;
  flex: 1;
  min-height: 0;
  border-radius: 16px;
  border: 1px solid #DDDDDD
}

.framework-grid {
  min-width: max-content;
}

.grid-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: auto;
}

.header-row {
  background-color: white;

  th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: white;
  }
}

.header-cell {
  padding: 10px 12px;
  font-weight: 600;
  font-size: 13px;
  text-align: left;
  white-space: nowrap;
  border-bottom: 2px solid #e0e0e0;
}

.level-header {
  position: sticky;
  left: 0;
  z-index: 1;
  background: white;
}

.track-header {
  text-align: center;
  min-width: 80px;
}

.loader {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.heatmap-table {
  border-collapse: collapse;
  width: 100%;
}

.heatmap-row {
  transition: background-color 0.15s;
  border-bottom: 1px solid #DDDDDD;
}

.heatmap-row-label {
  padding: 8px 12px;
  font-weight: 700;
  font-size: 14px;
  font-family: 'DM Sans' !important;
  letter-spacing: 0%;
  white-space: nowrap;
  color: #000000 !important;
}

.heatmap-cell {
  padding: 8px 12px;
  text-align: center;
  min-width: 80px;
  border-left: 1px solid #DDDDDD;
}
.heatmap-cell-count, .heatmap-cell-label {
  font-size: 14px;
  font-weight: 400;
  font-family: 'DM Sans' !important;
  color: #000000 !important;
  letter-spacing: 0;
}
.heatmap-cell-count {
  font-weight: 700;
}

.heatmap-cell-clickable {
  cursor: pointer;
  &:hover {
    filter: brightness(0.92);
  }
}

.heatmap-cell-empty {
  background: transparent;
}

.settings-anchor {
  position: relative;
  display: inline-flex;
}

.settings-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99;
}

.settings-menu {
  position: fixed;
  z-index: 100;
  background: #F7F9FB;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  border: 1px solid #e0e0e0;
  width: 280px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.settings-menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  border-bottom: 1px solid #e0e0e0;
}

.settings-menu-title {
  font-size: 14px;
  font-family: 'DM Sans' !important;
  font-weight: 700;
  color: #000000 !important;
}

.settings-menu-body {
  padding: 12px 16px;
  overflow-y: auto;
}

.settings-field {
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0;
  }
}

.action-button-small {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  font-size: 13px;
  background: transparent;
  border: 1px solid #ccc;
  border-radius: 6px;
  cursor: pointer;
  color: #333;
  &:hover {
    background: #f0f0f0;
  }
}
</style>
